import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '../../utils/cn';
import { MenuHighlightContext } from './MenuHighlightContext';

export interface MenuHighlightProps {
  /** The rows — `MenuListItem`s, labels, separators, anything. */
  children?: ReactNode;
  /** Classes for the list container (it owns the rows' layout). */
  className?: string;
  /** Row gap (px) when the caller needs one other than the panel's own 4. */
  gap?: number;
  /**
   * Index of the row the indicator should mark while nothing is hovered — for
   * the menu whose cursor lives in React state because focus belongs somewhere
   * else (a `@`-mention popup keeps the caret in the composer, so its rows can
   * never be focused). Omit for the usual focus-driven menus.
   *
   * The marked row is also scrolled into view, so an arrow-key cursor walking
   * past the fold behaves like a focused one.
   */
  activeIndex?: number;
}

/** Marker class every `MenuListItem` carries so the indicator can find rows. */
const ROW_SELECTOR = '.flashka-menu-row';

/** Travel time between rows. Long enough to read as motion, short enough that
 *  the indicator never lags a cursor moving down a list. */
const MOVE_MS = 150;
const FADE_MS = 120;
/** Decelerating — fast off the mark, settling into place. */
const MOVE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
  radius: string;
  /** `R, G, B` triple the hovered row publishes via `--menu-wash-rgb`. */
  wash: string;
}

/**
 * List wrapper that gives a menu ONE shared hover/focus highlight which slides
 * from row to row — **web**.
 *
 * Per-row `hover:bg-*` can't do this: each row fades its own background
 * independently, so the wash cross-dissolves rather than travels. Instead a
 * single absolutely-positioned element tracks whichever row the pointer (or
 * keyboard focus) is on and animates its `transform` / size between them.
 * Rows inside drop their own hover wash via {@link MenuHighlightContext}.
 *
 * The indicator renders BEFORE the rows so it paints behind them — row labels,
 * icons and any `selected` fill stay on top of it.
 *
 * It reads the target row's computed corner radius and `--menu-wash-rgb`, so a
 * menu mixing `secondary` rows with an `error` one gets the right colour and
 * shape per row for free, and dark mode resolves at read time.
 *
 * The native counterpart is a plain passthrough: RN has no hover, and a press
 * there is already a local, immediate state.
 */
export function MenuHighlight({
  children,
  className,
  gap,
  activeIndex,
}: MenuHighlightProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<HighlightRect | null>(null);
  const [visible, setVisible] = useState(false);
  // Position only animates when the indicator is ALREADY on screen — otherwise
  // it would fly in from wherever it was last parked. Mirrored in a ref because
  // `track` needs the value during the same event, before React re-renders.
  const [instant, setInstant] = useState(true);
  const visibleRef = useRef(false);
  // Read by `hide`, which must not clear a keyboard cursor just because the
  // pointer left the list.
  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;

  const trackedRowRef = useRef<HTMLElement | null>(null);

  const trackRow = useCallback((row: Element | null) => {
    const container = containerRef.current;
    if (!container || !(row instanceof HTMLElement) || !container.contains(row)) return;

    trackedRowRef.current = row;
    const rowBox = row.getBoundingClientRect();
    const box = container.getBoundingClientRect();
    // The popup's entrance animation scales the whole panel, and client rects
    // are measured post-transform — a row tracked during those 180ms (Radix
    // autofocuses a row the moment the popup opens) would park the indicator
    // at the scaled-down size. Divide the transform back out via the ratio of
    // the container's visual size to its layout size.
    const scaleX = box.width / container.offsetWidth || 1;
    const scaleY = box.height / container.offsetHeight || 1;
    const style = window.getComputedStyle(row);

    if (!visibleRef.current) {
      setInstant(true);
      // Next frame: the indicator is parked on the new row, so movement can
      // animate again from here on.
      requestAnimationFrame(() => setInstant(false));
    }
    visibleRef.current = true;
    setVisible(true);
    const top = (rowBox.top - box.top) / scaleY;
    const left = (rowBox.left - box.left) / scaleX;
    const width = rowBox.width / scaleX;
    const height = rowBox.height / scaleY;
    // Same rect → same object, so the `activeIndex` effect below can re-assert
    // its row after every render without looping through a state update.
    setRect((prev) =>
      prev &&
      prev.top === top &&
      prev.left === left &&
      prev.width === width &&
      prev.height === height
        ? prev
        : {
            top,
            left,
            width,
            height,
            radius: style.borderRadius,
            wash: style.getPropertyValue('--menu-wash-rgb').trim(),
          },
    );
  }, []);

  // A row tracked while the popup is still settling (autofocus fires before
  // Radix's `--radix-popover-trigger-width` min-width lands) measures the
  // panel at its natural width, and nothing re-renders afterwards to fix the
  // parked indicator. Re-measure the tracked row whenever the list's LAYOUT
  // size changes — transform animations don't fire this, the scale fix above
  // owns those.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const row = trackedRowRef.current;
      if (row?.isConnected) trackRow(row);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [trackRow]);

  const track = useCallback(
    (target: EventTarget | null) => {
      if (target instanceof Element) trackRow(target.closest(ROW_SELECTOR));
    },
    [trackRow],
  );

  /** Park the indicator on the caller's `activeIndex`. False when there is none. */
  const trackActive = useCallback((): boolean => {
    const index = activeIndexRef.current;
    if (index === undefined || !containerRef.current) return false;
    const row = containerRef.current.querySelectorAll(ROW_SELECTOR)[index];
    if (!row) return false;
    trackRow(row);
    row.scrollIntoView({ block: 'nearest' });
    return true;
  }, [trackRow]);

  // No dependency array on purpose: the rows themselves change as the caller
  // filters the list, so the mark has to be re-asserted after every render.
  // A no-op (and free) for the focus-driven menus that pass no `activeIndex`.
  useEffect(() => {
    trackActive();
  });

  const hide = useCallback(() => {
    if (trackActive()) return;
    trackedRowRef.current = null;
    visibleRef.current = false;
    setVisible(false);
  }, [trackActive]);

  return (
    <div
      ref={containerRef}
      className={cn('relative', className)}
      style={gap !== undefined ? { gap } : undefined}
      onPointerOver={(event) => track(event.target)}
      onPointerLeave={hide}
      // React's onFocus is the bubbling `focusin`, so keyboard navigation
      // between rows moves the indicator too. Only hide when focus leaves the
      // list entirely — moving between rows would otherwise flicker.
      onFocus={(event) => track(event.target)}
      onBlur={(event) => {
        const next = event.relatedTarget;
        if (!(next instanceof Node) || !containerRef.current?.contains(next)) hide();
      }}>
      <div
        aria-hidden
        className="pointer-events-none absolute left-0 top-0 backdrop-blur-sm motion-reduce:!transition-none"
        style={{
          transform: rect ? `translate3d(${rect.left}px, ${rect.top}px, 0)` : undefined,
          width: rect?.width,
          height: rect?.height,
          borderRadius: rect?.radius,
          // The row publishes its own wash; fall back to the neutral one for a
          // row that predates the token.
          backgroundColor: `rgba(${rect?.wash || 'var(--secondary-200-rgb)'}, 0.2)`,
          opacity: visible ? 1 : 0,
          transition: instant
            ? `opacity ${FADE_MS}ms ease-out`
            : `opacity ${FADE_MS}ms ease-out, transform ${MOVE_MS}ms ${MOVE_EASE}, ` +
              `width ${MOVE_MS}ms ${MOVE_EASE}, height ${MOVE_MS}ms ${MOVE_EASE}`,
        }}
      />
      <MenuHighlightContext.Provider value={true}>
        {children}
      </MenuHighlightContext.Provider>
    </div>
  );
}
